import React from 'react';

import { useFieldContext, FieldErrors } from '../';
import { cn } from '../../../utils';
import { Label } from '../../Elements/';

// Single-value select field using the native browser <select>. Lighter than SelectField (react-select),
// for simple lists of string options where search, multi-select and custom option rendering aren't needed.

export type SimpleSelectOption = {
	label: string;
	value: string;
	disabled?: boolean;
};

type SimpleSelectFieldProps = {
	label: string;
	options: SimpleSelectOption[];
	labelVisible?: boolean;
	labelClassName?: string;
	required?: boolean;
	className?: string;
	errorClassName?: string;
	placeholder?: string; // rendered as an empty first option
	id?: string;
} & Omit<
	React.SelectHTMLAttributes<HTMLSelectElement>,
	'value' | 'onChange' | 'onBlur' | 'name' | 'id' | 'multiple'
>;

export const SimpleSelectField = ({
	label,
	options,
	labelVisible = true,
	labelClassName,
	required,
	className,
	errorClassName,
	placeholder,
	id,
	...selectProps
}: SimpleSelectFieldProps) => {
	const field = useFieldContext<string>();

	// hide the error messages until the field has been touched
	const hideErrors = !field.state.meta.isTouched;
	const hasErrors = field.state.meta.errors.length > 0 && !hideErrors;
	const fieldId = id || field.name;

	return (
		<>
			<Label
				htmlFor={fieldId}
				label={label}
				required={required}
				labelSrOnly={!labelVisible}
				disabled={selectProps.disabled}
				className={cn('mb-2', labelClassName)}
			/>
			<select
				{...selectProps}
				id={fieldId}
				name={field.name}
				value={field.state.value ?? ''}
				onChange={(e) => field.handleChange(e.target.value)}
				onBlur={field.handleBlur}
				required={required}
				aria-invalid={hasErrors ? 'true' : 'false'}
				aria-errormessage={hasErrors ? `${field.name}-error` : undefined}
				className={cn(
					'block w-full h-11 px-3 rounded text-base',
					hasErrors
						? 'text-error border-error focus:ring-error focus:border-error'
						: 'border-primaryBorder focus:ring-primaryBrand focus:border-primaryBrand disabled:bg-disabledInputBackground disabled:text-disabledInputText disabled:border-disabledInputBorder disabled:cursor-not-allowed',
					className
				)}
			>
				{placeholder !== undefined && <option value=''>{placeholder}</option>}
				{options.map((option) => (
					<option key={option.value} value={option.value} disabled={option.disabled}>
						{option.label}
					</option>
				))}
			</select>
			<FieldErrors
				meta={field.state.meta}
				name={field.name}
				hideErrors={hideErrors}
				className={errorClassName}
			/>
		</>
	);
};
